/**
 * Pattern Game Component Types
 * Specialized types for Patterns discipline UI components
 */

import type { DisciplineSlug } from "@jumpbean/shared";

/**
 * Pattern element types
 */
export type PatternColor =
  | "red"
  | "orange"
  | "yellow"
  | "green"
  | "blue"
  | "purple"
  | "pink";

export type PatternShape =
  | "circle"
  | "square"
  | "triangle"
  | "star"
  | "heart"
  | "diamond";

export type PatternElement = PatternColor | PatternShape | string;

/**
 * Base props shared by all pattern game components
 */
export interface PatternGameBaseProps {
  /** Called when an answer is selected */
  onAnswer: (answer: PatternElement) => void;
  /** Currently selected answer */
  selectedAnswer: PatternElement | null;
  /** Whether answer has been submitted */
  isSubmitted?: boolean;
  /** Whether the submitted answer was correct */
  isCorrect?: boolean;
  /** Discipline for theming (defaults to patterns) */
  discipline?: DisciplineSlug;
}

/**
 * PatternStrip props - displays a pattern sequence with missing element
 */
export interface PatternStripProps extends PatternGameBaseProps {
  /** The full pattern including the missing element placeholder */
  pattern: PatternElement[];
  /** Index of the missing element in the pattern */
  missingIndex: number;
  /** Options to choose from to complete the pattern */
  options: PatternElement[];
  /** The correct answer */
  correctAnswer: PatternElement;
  /** Whether to animate the pattern reveal */
  animateReveal?: boolean;
}

/**
 * ColorPatternGame props - color-based pattern matching
 */
export interface ColorPatternGameProps extends Omit<PatternGameBaseProps, 'selectedAnswer' | 'onAnswer'> {
  /** Called when an answer is selected */
  onAnswer: (answer: PatternColor) => void;
  /** Currently selected answer */
  selectedAnswer: PatternColor | null;
  /** The color pattern with one missing */
  pattern: PatternColor[];
  /** Index of the missing color */
  missingIndex: number;
  /** Color options to choose from */
  options: PatternColor[];
  /** The correct color answer */
  correctAnswer: PatternColor;
}

/**
 * ShapePatternGame props - shape-based pattern matching
 */
export interface ShapePatternGameProps extends Omit<PatternGameBaseProps, 'selectedAnswer' | 'onAnswer'> {
  /** Called when an answer is selected */
  onAnswer: (answer: PatternShape) => void;
  /** Currently selected answer */
  selectedAnswer: PatternShape | null;
  /** The shape pattern with one missing */
  pattern: PatternShape[];
  /** Index of the missing shape */
  missingIndex: number;
  /** Shape options to choose from */
  options: PatternShape[];
  /** The correct shape answer */
  correctAnswer: PatternShape;
  /** Optional color for all shapes */
  shapeColor?: PatternColor;
}

/**
 * PatternTrain props - train-themed pattern completion
 */
export interface PatternTrainProps extends PatternGameBaseProps {
  /** The pattern elements in train cars */
  pattern: PatternElement[];
  /** Index of the missing element */
  missingIndex: number;
  /** Options to choose from */
  options: PatternElement[];
  /** The correct answer */
  correctAnswer: PatternElement;
  /** Type of pattern (color, shape, or mixed) */
  patternType: "color" | "shape" | "mixed";
}

/**
 * Pattern option item for displays
 */
export interface PatternOption {
  value: PatternElement;
  isSelected: boolean;
  isCorrect?: boolean;
  isDisabled?: boolean;
}

/**
 * PatternBuilder props - build a pattern from empty slots
 */
export interface PatternBuilderProps {
  /** Number of slots to fill */
  slotCount: number;
  /** Available pieces to choose from */
  availablePieces: PatternElement[];
  /** The correct pattern to build */
  correctPattern: PatternElement[];
  /** Called when the full pattern is built */
  onComplete: (pattern: PatternElement[]) => void;
  /** Called when pattern changes (for validation feedback) */
  onPatternChange?: (pattern: (PatternElement | null)[]) => void;
  /** Whether the pattern has been submitted */
  isSubmitted?: boolean;
  /** Whether the submitted pattern was correct */
  isCorrect?: boolean;
  /** Type of pattern elements */
  patternType: "color" | "shape" | "mixed";
}

/**
 * Color mapping for pattern colors
 */
export const PATTERN_COLORS: Record<PatternColor, { bg: string; border: string; glow: string }> = {
  red: {
    bg: "bg-gradient-to-br from-prism-red/80 via-prism-red to-prism-red-dark",
    border: "border-prism-red-dark",
    glow: "shadow-[0_0_20px_rgba(255,107,107,0.5)]",
  },
  orange: {
    bg: "bg-gradient-to-br from-prism-orange/80 via-prism-orange to-prism-orange-dark",
    border: "border-prism-orange-dark",
    glow: "shadow-[0_0_20px_rgba(255,179,71,0.5)]",
  },
  yellow: {
    bg: "bg-gradient-to-br from-prism-yellow/80 via-prism-yellow to-prism-yellow-dark",
    border: "border-prism-yellow-dark",
    glow: "shadow-[0_0_20px_rgba(255,230,109,0.5)]",
  },
  green: {
    bg: "bg-gradient-to-br from-prism-green/80 via-prism-green to-prism-green-dark",
    border: "border-prism-green-dark",
    glow: "shadow-[0_0_20px_rgba(152,216,155,0.5)]",
  },
  blue: {
    bg: "bg-gradient-to-br from-prism-blue/80 via-prism-blue to-prism-blue-dark",
    border: "border-prism-blue-dark",
    glow: "shadow-[0_0_20px_rgba(116,185,255,0.5)]",
  },
  purple: {
    bg: "bg-gradient-to-br from-prism-purple/80 via-prism-purple to-prism-purple-dark",
    border: "border-prism-purple-dark",
    glow: "shadow-[0_0_20px_rgba(162,155,254,0.5)]",
  },
  pink: {
    bg: "bg-gradient-to-br from-[#FFB8D8] via-[#FF9EBC] to-[#FF7EA0]",
    border: "border-[#EF6E90]",
    glow: "shadow-[0_0_20px_rgba(255,158,188,0.5)]",
  },
};

/**
 * Shape rendering configuration
 */
export const PATTERN_SHAPES: Record<PatternShape, { emoji: string; label: string }> = {
  circle: { emoji: "●", label: "circle" },
  square: { emoji: "■", label: "square" },
  triangle: { emoji: "▲", label: "triangle" },
  star: { emoji: "★", label: "star" },
  heart: { emoji: "♥", label: "heart" },
  diamond: { emoji: "◆", label: "diamond" },
};
